/* eslint-disable no-restricted-globals */
import React, { useCallback, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  EmployeeOfferDetails,
  MonetaryDetailsV2,
  PaymentDetails,
} from "../../../../types/Transaction";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../../components/utils";
import { Input } from "../../../../components/shared/InputField";
import {
  addExternalPaymentDetails,
  updatePaymentStatus,
} from "../../../../api/TransactionApisV2";

type AddExternalPaymentDetailsProps = {
  monetaryDetails: MonetaryDetailsV2;
  onClose: () => void;
  transactionId?: string;
  employee?: EmployeeOfferDetails;
  paymentDetails?: PaymentDetails[];
  approval?: boolean;
};

const paymentStatusConfig: Record<
  string,
  { label: string; className: string }
> = {
  PAYMENT_PENDING: {
    label: "Pending Approval",
    className: "bg-yellow-100 text-yellow-800 border border-yellow-200",
  },
  SUCCESS: {
    label: "Approved",
    className: "bg-green-100 text-green-800 border border-green-200",
  },
  FAILED: {
    label: "Rejected",
    className: "bg-red-100 text-red-800 border border-red-200",
  },
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status) return null;
  const config = paymentStatusConfig[status] || {
    label: status,
    className: "bg-gray-100 text-gray-700",
  };
  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}
    >
      {config.label}
    </span>
  );
};

const AddExternalPaymentDetails = (props: AddExternalPaymentDetailsProps) => {
  const {
    monetaryDetails,
    employee,
    onClose,
    paymentDetails = [],
    approval,
  } = props;

  const exercisePaymentLink = monetaryDetails.paymentLink;
  const taxPaymentLink = monetaryDetails.taxPaymentLink;
  const taxMandatoryForCompletion = monetaryDetails.taxMandatoryForCompletion;

  const exercisePayment = paymentDetails.find(
    (p) => p.paymentType.toLowerCase() === "exercise price"
  );
  const taxPayment = paymentDetails.find(
    (p) => p.paymentType.toLowerCase() === "tax price"
  );

  const exercisePaymentConfigured = exercisePaymentLink != null;
  const taxPaymentConfigured = taxPaymentLink != null;

  const showExerciseSection =
    exercisePaymentConfigured || Boolean(exercisePayment);
  const showTaxSection = taxPaymentConfigured || Boolean(taxPayment);

  const exerciseEditable =
    exercisePaymentConfigured &&
    ((!approval &&
      (!exercisePayment || exercisePayment.paymentStatus === "FAILED")) ||
      (approval && !exercisePayment));
  const taxEditable =
    taxPaymentConfigured &&
    ((!approval && (!taxPayment || taxPayment.paymentStatus === "FAILED")) ||
      (approval && !taxPayment));

  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isSubmitting = isApproving || isRejecting || isSaving;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toISOString().slice(0, 10);
  };

  const initialValues = {
    exercisePaymentDate: exercisePayment
      ? formatDate(
          typeof exercisePayment.paymentDate === "string"
            ? exercisePayment.paymentDate
            : exercisePayment.paymentDate?.toString() || ""
        )
      : "",
    exercisePaymentReferenceId: exercisePayment?.referenceId || "",
    exercisePaymentImage: undefined as File | undefined,
    taxPaymentDate: taxPayment
      ? formatDate(
          typeof taxPayment.paymentDate === "string"
            ? taxPayment.paymentDate
            : taxPayment.paymentDate?.toString() || ""
        )
      : "",
    taxPaymentReferenceId: taxPayment?.referenceId || "",
    taxPaymentImage: undefined as File | undefined,
  };

  // Validation only for add mode (not approval) and only for editable fields
  const validationSchemaFields: any = {};
  if (!approval) {
    if (exerciseEditable) {
      validationSchemaFields.exercisePaymentDate = Yup.string().required(
        "Payment Date is required"
      );
      validationSchemaFields.exercisePaymentReferenceId = Yup.string().required(
        "Reference ID is required"
      );
    }
    if (taxEditable && taxMandatoryForCompletion) {
      validationSchemaFields.taxPaymentDate = Yup.string().required(
        "Tax Payment Date is required"
      );
      validationSchemaFields.taxPaymentReferenceId = Yup.string().required(
        "Tax Reference ID is required"
      );
    }
  }

  const buildFormData = (values: typeof initialValues): FormData | null => {
    const payments: {
      paymentDate: string;
      paymentType: string;
      referenceId: string;
      currency: string;
      file?: File;
    }[] = [];

    if (
      exerciseEditable &&
      values.exercisePaymentDate &&
      values.exercisePaymentReferenceId
    ) {
      payments.push({
        paymentDate: values.exercisePaymentDate,
        paymentType: "Exercise Price",
        referenceId: values.exercisePaymentReferenceId,
        currency: "INR",
        file: values.exercisePaymentImage,
      });
    }
    if (taxEditable && values.taxPaymentDate && values.taxPaymentReferenceId) {
      payments.push({
        paymentDate: values.taxPaymentDate,
        paymentType: "Tax Price",
        referenceId: values.taxPaymentReferenceId,
        currency: "INR",
        file: values.taxPaymentImage,
      });
    }

    if (payments.length === 0) return null;

    const formData = new FormData();
    formData.append("employeeId", employee!.employeeId);
    formData.append("transactionId", props.transactionId!);
    payments.forEach((payment, idx) => {
      formData.append(
        `paymentDetails[${idx}][paymentDate]`,
        payment.paymentDate
      );
      formData.append(
        `paymentDetails[${idx}][paymentType]`,
        payment.paymentType
      );
      formData.append(
        `paymentDetails[${idx}][referenceId]`,
        payment.referenceId
      );
      formData.append(`paymentDetails[${idx}][currency]`, payment.currency);
      if (payment.file) {
        formData.append(`paymentDetails[${idx}][file]`, payment.file);
      }
    });
    return formData;
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object(validationSchemaFields),
    onSubmit: async (values) => {
      if (!employee?.employeeId || !props.transactionId) {
        toast.error("Details not found");
        return;
      }
      const formData = buildFormData(values);
      if (!formData) {
        toast.error("No new payment details to submit");
        return;
      }
      setIsSaving(true);
      try {
        await addExternalPaymentDetails(formData);
        toast.success("Payment details added successfully");
        onClose();
      } catch (err: any) {
        toast.error(
          err?.response?.data?.errorRaw || "Failed to add payment details"
        );
      } finally {
        setIsSaving(false);
      }
    },
  });

  const getPendingPaymentIds = (): string[] => {
    const ids: string[] = [];
    if (
      exercisePayment?.id &&
      exercisePayment.paymentStatus === "PAYMENT_PENDING"
    )
      ids.push(exercisePayment.id);
    if (taxPayment?.id && taxPayment.paymentStatus === "PAYMENT_PENDING")
      ids.push(taxPayment.id);
    return ids;
  };

  // In approval mode: submit any new details the admin entered, then approve all pending
  const handleApprove = async () => {
    if (!props.transactionId || !employee?.employeeId) {
      toast.error("Missing transaction or employee details");
      return;
    }
    setIsApproving(true);
    try {
      const newPaymentIds: string[] = [];
      const formData = buildFormData(formik.values);
      if (formData) {
        const res = await addExternalPaymentDetails(formData);
        const updatedEmployee = res.data.selectedEmployeesList?.find(
          (e: EmployeeOfferDetails) => e.employeeId === employee.employeeId
        );
        if (updatedEmployee?.paymentDetails) {
          if (exerciseEditable) {
            const ep = updatedEmployee.paymentDetails.find(
              (p: PaymentDetails) =>
                p.paymentType.toLowerCase() === "exercise price"
            );
            if (ep?.id) newPaymentIds.push(ep.id);
          }
          if (taxEditable) {
            const tp = updatedEmployee.paymentDetails.find(
              (p: PaymentDetails) => p.paymentType.toLowerCase() === "tax price"
            );
            if (tp?.id) newPaymentIds.push(tp.id);
          }
        }
      }

      const pendingIds = getPendingPaymentIds();
      const allIds = [...pendingIds, ...newPaymentIds];
      if (allIds.length === 0) {
        toast.error("No pending payments to approve");
        setIsApproving(false);
        return;
      }
      await updatePaymentStatus({
        transactionId: props.transactionId,
        employeeId: employee.employeeId,
        status: "SUCCESS",
        paymentDetailIds: allIds,
      });
      toast.success("Payment approved successfully");
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.errorRaw || "Failed to process payment");
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!props.transactionId || !employee?.employeeId) {
      toast.error("Missing transaction or employee details");
      return;
    }
    const pendingIds = getPendingPaymentIds();
    if (pendingIds.length === 0) {
      toast.error("No pending payments to reject");
      return;
    }
    setIsRejecting(true);
    try {
      await updatePaymentStatus({
        transactionId: props.transactionId,
        employeeId: employee.employeeId,
        status: "FAILED",
        paymentDetailIds: pendingIds,
      });
      toast.success("Payment rejected successfully");
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.errorRaw || "Failed to reject payment");
    } finally {
      setIsRejecting(false);
    }
  };

  const handleDownload = useCallback((s3Url: string) => {
    if (s3Url) {
      window.open(s3Url, "_blank");
    } else {
      toast.error("Failed to get download link");
    }
  }, []);

  const hasPendingPayments = getPendingPaymentIds().length > 0;
  const approvalHasNewPayments =
    approval &&
    ((exerciseEditable &&
      Boolean(formik.values.exercisePaymentDate) &&
      Boolean(formik.values.exercisePaymentReferenceId)) ||
      (taxEditable &&
        Boolean(formik.values.taxPaymentDate) &&
        Boolean(formik.values.taxPaymentReferenceId)));

  const showApproveButton =
    approval && (hasPendingPayments || approvalHasNewPayments);
  const showRejectButton = approval && hasPendingPayments;
  const showSaveButton = !approval && (exerciseEditable || taxEditable);

  const renderPaymentSection = (
    type: "exercise" | "tax",
    payment: PaymentDetails | undefined,
    link: string | undefined,
    show: boolean
  ) => {
    if (!show) return null;
    const isExercise = type === "exercise";
    const label = isExercise ? "Exercise Payment" : "Tax Payment";
    const dateField = isExercise ? "exercisePaymentDate" : "taxPaymentDate";
    const refField = isExercise
      ? "exercisePaymentReferenceId"
      : "taxPaymentReferenceId";
    const fileField = isExercise ? "exercisePaymentImage" : "taxPaymentImage";
    const editable = isExercise ? exerciseEditable : taxEditable;

    return (
      <VStack className="gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <HStack className="items-center justify-between">
          <span className="text-sm font-semibold text-gray-800">{label}</span>
          {payment ? (
            <StatusBadge status={payment.paymentStatus} />
          ) : (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
              Not Submitted
            </span>
          )}
        </HStack>
        {/* Rejection notice — shown in add mode when last payment was rejected */}
        {payment?.paymentStatus === "FAILED" && editable && (
          <div className="flex items-start gap-2 px-3 py-2 text-xs text-red-700 border border-red-200 rounded-md bg-red-50">
            <span className="mt-0.5">⚠</span>
            <span>
              The previous payment was <strong>rejected</strong>. The details
              are pre-filled below — edit if needed and save to re-submit for
              approval.
            </span>
          </div>
        )}
        <HStack className="gap-4">
          <VStack className="flex-1">
            <label className="mb-1 text-xs font-medium text-gray-500">
              Payment Date
            </label>
            {editable ? (
              <>
                <Input
                  type="date"
                  name={dateField}
                  className="w-full"
                  value={(formik.values as any)[dateField]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isSubmitting}
                />
                {formik.touched[dateField as keyof typeof formik.touched] &&
                  typeof formik.errors[
                    dateField as keyof typeof formik.errors
                  ] === "string" && (
                    <div className="mt-1 text-xs text-red-500">
                      {
                        formik.errors[
                          dateField as keyof typeof formik.errors
                        ] as string
                      }
                    </div>
                  )}
              </>
            ) : (
              <div className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-md">
                {(formik.values as any)[dateField] || "—"}
              </div>
            )}
          </VStack>
          <VStack className="flex-1">
            <label className="mb-1 text-xs font-medium text-gray-500">
              Reference ID
            </label>
            {editable ? (
              <>
                <Input
                  type="text"
                  name={refField}
                  placeholder="Enter Reference ID"
                  className="w-full"
                  value={(formik.values as any)[refField]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isSubmitting}
                />
                {formik.touched[refField as keyof typeof formik.touched] &&
                  typeof formik.errors[
                    refField as keyof typeof formik.errors
                  ] === "string" && (
                    <div className="mt-1 text-xs text-red-500">
                      {
                        formik.errors[
                          refField as keyof typeof formik.errors
                        ] as string
                      }
                    </div>
                  )}
              </>
            ) : (
              <div className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-md">
                {(formik.values as any)[refField] || "—"}
              </div>
            )}
          </VStack>
          <VStack className="flex-1">
            <label className="mb-1 text-xs font-medium text-gray-500">
              Payment Proof
            </label>
            {payment?.documentLinks?.[0]?.s3Url ? (
              <button
                type="button"
                className="text-sm text-left text-blue-600 underline hover:text-blue-800"
                onClick={() =>
                  handleDownload(payment.documentLinks![0].s3Url || "")
                }
              >
                {payment.documentLinks[0].name || "View Document"}
              </button>
            ) : editable ? (
              <Input
                type="file"
                accept="image/*,application/pdf"
                name={fileField}
                className="w-full"
                onChange={(e) =>
                  formik.setFieldValue(fileField, e.currentTarget.files?.[0])
                }
                disabled={isSubmitting}
              />
            ) : (
              <div className="px-3 py-2 text-sm text-gray-400 bg-white border border-gray-200 rounded-md">
                No document uploaded
              </div>
            )}
          </VStack>
        </HStack>
      </VStack>
    );
  };

  return (
    <div className="w-[700px] mx-auto bg-white rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-5 border-b">
        <div>
          <h6 className="text-base font-semibold text-gray-900">
            {approval ? "Review Payment Details" : "Add Payment Details"}
          </h6>
          <p className="mt-0.5 text-sm text-gray-500">
            {employee?.employeeName || "Employee"}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="text-xl leading-none text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <VStack className="gap-4 p-6">
          {!showExerciseSection && !showTaxSection && (
            <div className="py-8 text-sm text-center text-gray-500">
              No payment details configured for this transaction.
            </div>
          )}

          {/* Info banner in approval mode when admin can enter new details */}
          {approval && (exerciseEditable || taxEditable) && (
            <div className="px-4 py-3 text-sm text-blue-700 border border-blue-100 rounded-lg bg-blue-50">
              No payment details have been submitted yet. You can enter them
              below and approve directly.
            </div>
          )}

          {renderPaymentSection(
            "exercise",
            exercisePayment,
            exercisePaymentLink,
            showExerciseSection
          )}
          {renderPaymentSection(
            "tax",
            taxPayment,
            taxPaymentLink,
            showTaxSection
          )}

          <HStack className="justify-between gap-2 pt-2 border-t border-gray-100">
            <ButtonPrimary1
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </ButtonPrimary1>
            <HStack className="gap-2">
              {showRejectButton && (
                <ButtonPrimary1
                  type="button"
                  onClick={handleReject}
                  loading={isRejecting}
                  disabled={isSubmitting}
                  loadingText="Rejecting..."
                >
                  Reject
                </ButtonPrimary1>
              )}
              {showApproveButton && (
                <ButtonPrimary
                  type="button"
                  onClick={handleApprove}
                  loading={isApproving}
                  disabled={isSubmitting}
                  loadingText="Processing..."
                >
                  {approvalHasNewPayments && !hasPendingPayments
                    ? "Save & Approve"
                    : "Approve"}
                </ButtonPrimary>
              )}
              {showSaveButton && (
                <ButtonPrimary
                  type="submit"
                  loading={isSaving}
                  disabled={isSubmitting}
                  loadingText="Saving..."
                >
                  Save
                </ButtonPrimary>
              )}
            </HStack>
          </HStack>
        </VStack>
      </form>
    </div>
  );
};

export default AddExternalPaymentDetails;
